import { useEffect } from "react";
import L from "leaflet";
import { CircleMarker, MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

const iconCache = {};
const iconFor = (bucket) => {
  if (!iconCache[bucket]) {
    iconCache[bucket] = L.divIcon({
      className: "",
      html: `<div class="im im-${bucket}"><i></i></div>`,
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });
  }
  return iconCache[bucket];
};

const warnIconCache = {};
const warnIconFor = (bucket) => {
  if (!warnIconCache[bucket]) {
    warnIconCache[bucket] = L.divIcon({
      className: "",
      html: `<div class="wm wm-${bucket}"></div>`,
      iconSize: [16, 16],
      iconAnchor: [8, 8],
    });
  }
  return warnIconCache[bucket];
};

function FitBounds({ points }) {
  const map = useMap();
  const key = points.map((p) => p.id).sort().join(",");
  useEffect(() => {
    if (!points.length) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds.pad(0.25), { maxZoom: 10 });
  }, [key]);
  return null;
}

export default function MapView({ incidents, stations, warnings = [], panelOpen = false }) {
  return (
    <div
      data-testid="map-view"
      className="relative h-[420px] overflow-hidden border border-[#27272A] lg:h-[560px]"
    >
      <MapContainer
        center={[-37.83, 144.97]}
        zoom={10}
        scrollWheelZoom={false}
        className="z-0 h-full w-full"
      >
        <TileLayer
          attribution="&copy; OpenStreetMap &copy; CARTO"
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        />
        {stations.map((s) => (
          <CircleMarker
            key={`${s.agency}-${s.code}`}
            center={[s.lat, s.lng]}
            radius={2.5}
            pathOptions={{
              color: s.agency === "FRV" ? "#FF6B00" : "#71717A",
              fillColor: s.agency === "FRV" ? "#FF6B00" : "#52525B",
              fillOpacity: 0.55,
              opacity: 0.5,
              weight: 1,
            }}
          />
        ))}
        {incidents
          .filter((inc) => inc.lat && inc.lng)
          .map((inc) => (
          <Marker key={inc.id} position={[inc.lat, inc.lng]} icon={iconFor(inc.bucket)}>
            <Popup>
              <div>
                <div style={{ color: "#FF5247", fontWeight: 700 }}>
                  {inc.code} · {inc.type}
                </div>
                <div>
                  {inc.address}
                  {inc.locality ? ` — ${inc.locality}` : ""}
                </div>
                <div style={{ color: "#9CA3AF" }}>
                  {inc.agency} — {inc.status.replace("_", " ")}
                </div>
                <div style={{ color: "#71717A", marginTop: 4 }}>
                  {inc.appliances.map((a) => a.callsign).join(" · ")}
                  {inc.channels.length ? ` · ${inc.channels.join(", ")}` : ""}
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
        <FitBounds points={incidents.filter((inc) => inc.lat && inc.lng)} />
        {warnings.map((w) => (
          <Marker
            key={w.id}
            position={[w.lat, w.lng]}
            icon={warnIconFor(w.bucket)}
            zIndexOffset={500}
          >
            <Popup>
              <div>
                <div style={{ color: "#FFC107", fontWeight: 700 }}>
                  {w.severity}
                  {w.category ? ` — ${w.category}` : ""}
                </div>
                <div>{w.title}</div>
                <div style={{ color: "#9CA3AF" }}>{w.location}</div>
                <div style={{ color: "#71717A", marginTop: 4 }}>
                  OFFICIAL VICEMERGENCY WARNING
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      {!panelOpen && (
        <div className="pointer-events-none absolute bottom-4 left-4 z-[500] border border-[#27272A] bg-black/70 px-4 py-3 backdrop-blur-md">
        <p className="font-jb text-[9px] tracking-[0.3em] text-zinc-500">LEGEND</p>
        <div className="mt-2 space-y-1.5 font-jb text-[10px] text-zinc-400">
          <p><span className="mr-2 inline-block h-2 w-2 rounded-full bg-[#FF3B30]" />RESPONDING</p>
          <p><span className="mr-2 inline-block h-2 w-2 rounded-full bg-[#FFC107]" />ON SCENE</p>
          <p><span className="mr-2 inline-block h-2 w-2 rounded-full bg-[#10B981]" />STOPPED</p>
          <p><span className="mr-2 inline-block h-2 w-2 rounded-full bg-[#FF6B00]" />FRV STATION</p>
          <p><span className="mr-2 inline-block h-2 w-2 rounded-full bg-[#71717A]" />CFA BRIGADE</p>
          <p className="pt-1 text-[#FF3B30]">▲ <span className="text-zinc-400">EMERGENCY WARNING</span></p>
          <p className="text-[#FFC107]">▲ <span className="text-zinc-400">WATCH &amp; ACT</span></p>
          <p className="text-[#3B82F6]">▲ <span className="text-zinc-400">ADVICE</span></p>
        </div>
        </div>
      )}
    </div>
  );
}
